(function () {
  "use strict";

  const COUNTRY_NAMES = { DE: "Deutschland", AT: "Österreich", CH: "Schweiz" };
  const DEFAULT_CENTER = [10.4, 49.2];
  const DEFAULT_ZOOM = 4.6;

  function normalize(value) {
    return String(value || "")
      .trim()
      .toLocaleLowerCase("de-DE")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  // Translated UI strings come from sections/stricktreffen-map.liquid via window.gwaStrings.
  // Looked up at call time; the German text is the fallback. Placeholders use [name] syntax.
  function str(key, fallback, vars) {
    const strings = (window.gwaStrings && window.gwaStrings.stricktreffen_map) || {};
    const text = typeof strings[key] === "string" && strings[key] ? strings[key] : fallback;
    if (!vars) return text;
    return text.replace(/\[(\w+)\]/g, (match, name) => (Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match));
  }

  function countryName(code) {
    const keys = { DE: "country_de", AT: "country_at", CH: "country_ch" };
    return keys[code] ? str(keys[code], COUNTRY_NAMES[code]) : code;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function safeUrl(value, protocols) {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.origin);
      return protocols.includes(url.protocol) ? url.href : "";
    } catch (_error) {
      return "";
    }
  }

  function iconTemplate(name) {
    const icons = {
      location: '<path d="M12 21s6-5.2 6-12a6 6 0 1 0-12 0c0 6.8 6 12 6 12Z"/><circle cx="12" cy="9" r="2.2"/>',
      clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7v5l3.5 2"/>',
      people: '<circle cx="9" cy="9" r="3"/><path d="M3.5 20c.5-4 2.3-6 5.5-6s5 2 5.5 6M15 7.5a2.5 2.5 0 0 1 0 5M16.5 14.5c2.3.6 3.6 2.4 4 5.5"/>',
      info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5h.01"/>',
      person: '<circle cx="12" cy="8" r="3"/><path d="M5.5 20c.7-4.2 2.8-6.3 6.5-6.3s5.8 2.1 6.5 6.3"/>',
      mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m4 7 8 6 8-6"/>',
      phone: '<path d="M7.2 3.5 4.4 5c-.8.5-.9 1.4-.6 2.3 2.1 6.2 6.7 10.8 12.9 12.9.9.3 1.8.2 2.3-.6l1.5-2.8-4.2-2.1-1.5 2c-3.4-1.5-6-4.1-7.5-7.5l2-1.5-2.1-4.2Z"/>',
      link: '<path d="M10 14 8.5 15.5a4 4 0 0 1-5.7-5.7L6 6.6a4 4 0 0 1 5.7 0M14 10l1.5-1.5a4 4 0 1 1 5.7 5.7L18 17.4a4 4 0 0 1-5.7 0"/>',
      flag: '<path d="M5 21V4m0 1h11l-2 3 2 3H5"/>',
    };
    return `<svg class="stricktreffen-map__icon" viewBox="0 0 24 24" aria-hidden="true">${icons[name] || icons.info}</svg>`;
  }

  function haversine(a, b) {
    const radius = 6371;
    const toRad = (degrees) => (degrees * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * radius * Math.asin(Math.sqrt(h));
  }

  function asBoolean(value) {
    return value === true || value === "true" || value === 1 || value === "1";
  }

  function asOptionalBoolean(value) {
    if (value === null || value === undefined || value === "") return null;
    return asBoolean(value);
  }

  function hasPreciseCoordinates(meeting) {
    const precision = cleanCoordinatePrecision(meeting.coordinatePrecision);
    return hasCoordinates(meeting) && (!precision || ["address", "street", "manual", "exact"].includes(precision));
  }

  function hasCoordinates(meeting) {
    return cleanCoordinatePrecision(meeting.coordinatePrecision) !== "unknown" && coordinatesMatchAddress(meeting) && Number.isFinite(meeting.lat) && Number.isFinite(meeting.lon) &&
      Math.abs(meeting.lat) <= 90 && Math.abs(meeting.lon) <= 180;
  }

  function coordinatesMatchAddress(meeting) {
    if (cleanCoordinatePrecision(meeting.coordinatePrecision) === "manual") return true;
    if (!("checkedAddress" in meeting)) return true;
    const checked = meeting.checkedAddress;
    if (!checked || typeof checked !== "object" || cleanCoordinatePrecision(meeting.coordinatePrecision) === "unknown") return false;
    const norm = value => String(value ?? "").trim().toLowerCase();
    return [["strasse_hausnummer", "street"], ["postleitzahl", "postalCode"], ["ort", "city"], ["land", "country"]]
      .every(([stored, current]) => norm(checked[stored]) === norm(meeting[current]));
  }

  function coordinateNumber(value) {
    return value === null || value === undefined || String(value).trim() === "" ? null : Number(value);
  }

  function approximatePlace(meeting, places) {
    const country = ({ de: "DE", deutschland: "DE", germany: "DE", at: "AT", osterreich: "AT", oesterreich: "AT", austria: "AT", ch: "CH", schweiz: "CH", switzerland: "CH" })[normalize(meeting.country)];
    if (!country) return null;
    const postalCode = String(meeting.postalCode || "").trim();
    const city = normalize(meeting.city);
    const valid = places.filter(place => place.country === country && Number.isFinite(place.lat) && Number.isFinite(place.lon) && place.lat >= 45 && place.lat <= 56 && place.lon >= 5 && place.lon <= 18);
    const postal = postalCode ? valid.filter(place => place.postalCode === postalCode) : [];
    const named = (postal.length ? postal : valid).filter(place => city && normalize(place.name) === city);
    const candidates = named.length ? named : postal;
    if (!candidates.length) return null;
    // Never resolve ambiguous distant names to an arbitrary town. Postcode-only
    // matches remain usable when the entered town contains a spelling mistake.
    if (candidates.some(place => haversine(candidates[0], place) > 50)) return null;
    const center = candidates.reduce((sum, place) => ({lat: sum.lat + place.lat, lon: sum.lon + place.lon}), {lat: 0, lon: 0});
    return {lat: center.lat / candidates.length, lon: center.lon / candidates.length};
  }

  function cleanCoordinatePrecision(value) {
    return String(value || "").trim().toLowerCase();
  }

  function initMultistepForm(container) {
    if (!container || container.dataset.multistepReady === "true") return;
    const form = container.matches("form") ? container : container.querySelector("form");
    if (!form) return;

    const groups = Array.from(form.querySelectorAll("[data-form-step]"));
    const steps = [...new Set(groups.map((group) => Number(group.dataset.formStep)))].filter(Boolean).sort((a, b) => a - b);
    if (steps.length < 2) return;

    container.dataset.multistepReady = "true";
    const labels = steps.map((step) => groups.find((group) => Number(group.dataset.formStep) === step)?.dataset.stepTitle || str("step_fallback", "Schritt [step]", { step }));
    const progress = document.createElement("div");
    progress.className = "stricktreffen-map__form-progress";
    progress.setAttribute("aria-label", str("form_progress_aria", "Fortschritt der Einreichung"));

    const status = document.createElement("p");
    status.className = "stricktreffen-map__form-progress-status";
    status.setAttribute("aria-live", "polite");

    const progressList = document.createElement("ol");
    progressList.className = "stricktreffen-map__form-progress-list";
    const progressButtons = steps.map((step, index) => {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "stricktreffen-map__form-progress-step";
      button.dataset.formStepTarget = String(step);
      button.innerHTML = `<span>${step}</span><small>${escapeHtml(labels[index])}</small>`;
      item.append(button);
      progressList.append(item);
      return button;
    });

    const track = document.createElement("div");
    track.className = "stricktreffen-map__form-progress-track";
    track.setAttribute("aria-hidden", "true");
    const trackValue = document.createElement("span");
    track.append(trackValue);
    progress.append(status, progressList, track);

    const finalGroups = groups.filter((group) => Number(group.dataset.formStep) === steps[steps.length - 1]);
    const summary = document.createElement("div");
    summary.className = "stricktreffen-map__form-summary";
    summary.innerHTML = `<p class="stricktreffen-map__form-summary-title">${escapeHtml(str("summary_title", "Deine Angaben auf einen Blick"))}</p><dl></dl>`;
    finalGroups[0]?.prepend(summary);

    const actions = document.createElement("div");
    actions.className = "stricktreffen-map__form-actions";
    const previous = document.createElement("button");
    previous.type = "button";
    previous.className = "stricktreffen-map__button stricktreffen-map__button--secondary";
    previous.textContent = str("back", "Zurück");
    const next = document.createElement("button");
    next.type = "button";
    next.className = "stricktreffen-map__button";
    next.textContent = str("next", "Weiter");
    const submit = form.querySelector('button[type="submit"]');
    actions.append(previous, next);
    if (submit) actions.append(submit);
    form.append(actions);
    form.prepend(progress);

    let currentIndex = 0;
    let furthestIndex = 0;

    const fieldsForStep = (step) => groups
      .filter((group) => Number(group.dataset.formStep) === step)
      .flatMap((group) => Array.from(group.querySelectorAll("input, select, textarea")))
      .filter((field) => !field.disabled && field.type !== "hidden");

    const validateStep = (step) => {
      const invalid = fieldsForStep(step).find((field) => !field.checkValidity());
      if (!invalid) return true;
      invalid.reportValidity();
      invalid.focus({ preventScroll: false });
      return false;
    };

    const updateSummary = () => {
      const definitions = [
        [str("step_title_meeting", "Treffen"), "title"],
        [str("label_city", "Ort"), "city"],
        [str("summary_date", "Datum"), "date"],
        [str("label_time", "Uhrzeit"), "time"],
        [str("summary_recurrence", "Rhythmus"), "recurrence"],
        [str("label_cost", "Kosten"), "cost"],
        [str("summary_registration", "Anmeldung"), "registration"],
        [str("step_title_contact", "Kontakt"), "name"],
        [str("email", "E-Mail"), "email"],
      ];
      const list = summary.querySelector("dl");
      list.replaceChildren();
      definitions.forEach(([label, key]) => {
        const field = form.querySelector(`#demo-${key}, [id^="submission-${key}-"]`);
        if (!field || !field.value) return;
        const term = document.createElement("dt");
        const value = document.createElement("dd");
        term.textContent = label;
        value.textContent = field.tagName === "SELECT" ? field.options[field.selectedIndex]?.text || field.value : field.value;
        list.append(term, value);
      });
    };

    const showStep = (index, focusHeading = false) => {
      currentIndex = Math.max(0, Math.min(index, steps.length - 1));
      furthestIndex = Math.max(furthestIndex, currentIndex);
      const currentStep = steps[currentIndex];

      groups.forEach((group) => {
        const active = Number(group.dataset.formStep) === currentStep;
        group.hidden = !active;
        group.setAttribute("aria-hidden", String(!active));
      });
      progressButtons.forEach((button, buttonIndex) => {
        button.dataset.state = buttonIndex < currentIndex ? "complete" : buttonIndex === currentIndex ? "active" : "upcoming";
        button.setAttribute("aria-current", buttonIndex === currentIndex ? "step" : "false");
        button.disabled = buttonIndex > furthestIndex;
      });
      status.textContent = str("step_status", "Schritt [current] von [total] · [label]", { current: currentIndex + 1, total: steps.length, label: labels[currentIndex] });
      trackValue.style.width = `${((currentIndex + 1) / steps.length) * 100}%`;
      previous.hidden = currentIndex === 0;
      next.hidden = currentIndex === steps.length - 1;
      if (submit) submit.hidden = currentIndex !== steps.length - 1;
      if (currentIndex === steps.length - 1) updateSummary();

      if (focusHeading) {
        const legend = groups.find((group) => Number(group.dataset.formStep) === currentStep)?.querySelector("legend");
        if (legend) {
          legend.tabIndex = -1;
          legend.focus({ preventScroll: true });
        }
      }
    };

    previous.addEventListener("click", () => showStep(currentIndex - 1, true));
    next.addEventListener("click", () => {
      if (!validateStep(steps[currentIndex])) return;
      showStep(currentIndex + 1, true);
    });
    progressButtons.forEach((button, index) => {
      button.addEventListener("click", () => {
        if (index <= furthestIndex) showStep(index, true);
      });
    });
    form.addEventListener("submit", (event) => {
      const invalid = Array.from(form.elements).find((field) => typeof field.checkValidity === "function" && !field.checkValidity());
      if (!invalid) return;
      event.preventDefault();
      const group = invalid.closest("[data-form-step]");
      const targetStep = Number(group?.dataset.formStep);
      const targetIndex = steps.indexOf(targetStep);
      if (targetIndex >= 0) showStep(targetIndex, true);
      window.setTimeout(() => invalid.reportValidity(), 0);
    });

    container.classList.add("is-multistep-ready");
    showStep(0);
  }

  function initFaqAccordions(scope) {
    scope.querySelectorAll(".stricktreffen-map__faq").forEach((list) => {
      if (list.dataset.accordionReady === "true") return;
      list.dataset.accordionReady = "true";
      list.querySelectorAll("details").forEach((item) => {
        item.addEventListener("toggle", () => {
          if (!item.open) return;
          list.querySelectorAll("details[open]").forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  class StricktreffenMap {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.sectionId || Math.random().toString(36).slice(2);
      this.meetings = this.parseMeetings();
      this.places = [];
      this.markers = new Map();
      this.filteredMeetings = this.meetings.map((meeting) => ({ ...meeting, distance: null }));
      this.searchCenter = null;
      this.activeMeetingId = null;
      this.activeSuggestionIndex = -1;
      this.abortController = null;
      this.map = null;
      this.mapReady = false;
      this.onDocumentKeydown = (event) => {
        if (event.key === "Escape" && this.elements.finder?.dataset.fullscreen === "true") {
          this.toggleMapFullscreen(false);
        }
      };
      this.elements = this.collectElements();
    }

    collectElements() {
      const query = (selector) => this.root.querySelector(selector);
      return {
        searchForm: query("[data-search-form]"),
        searchPanel: query("[data-search-panel]"),
        searchPanelToggle: query("[data-search-panel-toggle]"),
        searchPanelToggleLabel: query("[data-search-panel-toggle-label]"),
        searchInput: query("[data-place-search]"),
        suggestions: query("[data-place-suggestions]"),
        radius: query("[data-radius]"),
        locate: query("[data-locate]"),
        filterToggle: query("[data-filter-toggle]"),
        filters: query("[data-filters]"),
        filterInputs: Array.from(this.root.querySelectorAll("[data-filter]")),
        reset: query("[data-reset]"),
        status: query("[data-status]"),
        resultsCount: query("[data-results-count]"),
        results: query("[data-results]"),
        finder: query("[data-finder]"),
        layout: query("[data-results-layout]"),
        mapWrap: query("[data-map-wrap]"),
        map: query("[data-map]"),
        mapMessage: query("[data-map-message]"),
        mapFullscreen: query("[data-map-fullscreen]"),
        mapFullscreenLabel: query("[data-map-fullscreen-label]"),
        dialog: query("[data-detail-dialog]"),
        dialogContent: query("[data-detail-content]"),
        dialogClose: query("[data-detail-close]"),
        dialogPrevious: query("[data-detail-previous]"),
        dialogNext: query("[data-detail-next]"),
        dialogPosition: query("[data-detail-position]"),
        reportDialog: query("[data-report-dialog]"),
        reportDialogClose: query("[data-report-close]"),
        reportMeeting: query("[data-report-meeting]"),
        reportMeetingDisplay: query("[data-report-meeting-display]"),
        reportEmail: query("[data-report-dialog] input[type='email']"),
        reportFeedback: query("[data-report-success], [data-report-errors]"),
        submissionSuccess: query("[data-submission-success]"),
        viewButtons: Array.from(this.root.querySelectorAll("[data-view-button]")),
      };
    }

    normalizeMeetings(items) {
      if (!Array.isArray(items)) return [];
      return items
        .map((item, index) => ({
          ...item,
          id: String(item.id || `meeting-${index}`),
          lat: coordinateNumber(item.lat),
          lon: coordinateNumber(item.lon),
          knitting: asBoolean(item.knitting),
          crochet: asBoolean(item.crochet),
          crafts: asBoolean(item.crafts),
          beginner: asOptionalBoolean(item.beginner),
          free: asOptionalBoolean(item.free),
          accessible: asOptionalBoolean(item.accessible),
          registration: asOptionalBoolean(item.registration),
          socialProject: asBoolean(item.socialProject || item.maschenFuerGesellschaft || item.maschen_fuer_die_gesellschaft),
        }));
    }

    parseMeetings() {
      const script = this.root.querySelector("script[data-meetings]");
      if (!script) return [];
      try {
        return this.normalizeMeetings(JSON.parse(script.textContent));
      } catch (error) {
        console.error("Stricktreffen-Daten konnten nicht gelesen werden.", error);
        return [];
      }
    }

    async loadExternalMeetings() {
      if (this.root.dataset.meetingsSource === "metaobjects") {
        await this.loadMeetingPages();
        await this.loadLegacyGallery();
        return;
      }
      const url = this.root.dataset.meetingsUrl;
      if (!url) return;
      try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.meetings = this.normalizeMeetings(await response.json());
        this.filteredMeetings = this.meetings.map((meeting) => ({ ...meeting, distance: null }));
      } catch (error) {
        console.error("Externe Stricktreffen-Daten konnten nicht geladen werden.", error);
      }
    }

    async loadMeetingPages() {
      let next = this.root.querySelector("script[data-meetings]")?.dataset.nextPage;
      const visited = new Set();
      const meetings = new Map(this.meetings.map(meeting => [meeting.id, meeting]));
      try {
        while (next) {
          const url = new URL(next, window.location.href);
          if (url.origin !== window.location.origin) throw new Error("Unexpected pagination origin");
          url.searchParams.set("section_id", this.root.dataset.sectionId);
          const current = new URL(window.location.href);
          ["preview_key", "preview_theme_id"].forEach(key => {
            if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
          });
          if (visited.has(url.href)) throw new Error("Repeated meeting page");
          visited.add(url.href);
          const response = await fetch(url.href, { credentials: "same-origin" });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const document = new DOMParser().parseFromString(await response.text(), "text/html");
          const section = Array.from(document.querySelectorAll("[data-stricktreffen-map]"))
            .find(element => element.dataset.sectionId === this.root.dataset.sectionId);
          const script = section?.querySelector("script[data-meetings]");
          if (!script) throw new Error("Meeting page missing");
          const items = JSON.parse(script.textContent);
          if (!Array.isArray(items)) throw new Error("Invalid meeting page");
          this.normalizeMeetings(items).forEach(meeting => meetings.set(meeting.id, meeting));
          next = script.dataset.nextPage;
        }
        this.meetings = [...meetings.values()];
        this.filteredMeetings = this.meetings.map(meeting => ({ ...meeting, distance: null }));
      } catch (error) {
        console.error("Weitere Stricktreffen konnten nicht geladen werden.", error);
        this.root.dataset.meetingsLoadError = "true";
      }
    }

    async loadLegacyGallery() {
      const url = this.root.dataset.legacyGalleryUrl;
      if (!url) return;
      try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) return;
        const items = await response.json();
        if (!Array.isArray(items)) return;
        const galleries = new Map(items.filter(item => item.imageUrls?.length).map(item => [item.id, item.imageUrls]));
        this.meetings.forEach(meeting => {
          if (!meeting.imageUrl && !meeting.imageUrls?.length && galleries.has(meeting.id)) {
            meeting.imageUrls = galleries.get(meeting.id);
          }
        });
        this.filteredMeetings = this.meetings.map(meeting => ({ ...meeting, distance: null }));
      } catch (error) {
        console.error("Vorhandene Galeriebilder konnten nicht geladen werden.", error);
      }
    }

    async init() {
      if (this.root.dataset.initialized === "true") return;
      this.root.dataset.initialized = "true";
      this.root.querySelectorAll("[data-multistep-form]").forEach(initMultistepForm);
      this.bindEvents();
      this.showPostedFormFeedback();
      await this.loadExternalMeetings();
      this.renderResults();
      const tasks = await Promise.allSettled([this.loadPlaces(), this.initMap()]);
      tasks.forEach((task) => {
        if (task.status === "rejected") console.error("Stricktreffen-Komponente konnte nicht vollständig initialisiert werden.", task.reason);
      });
    }

    bindEvents() {
      this.elements.searchForm?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.applyTypedSearch();
      });

      this.elements.searchInput?.addEventListener("input", () => this.showSuggestions());
      this.elements.searchInput?.addEventListener("keydown", (event) => this.onSearchKeydown(event));
      this.elements.searchInput?.addEventListener("blur", () => {
        window.setTimeout(() => this.hideSuggestions(), 150);
      });

      this.elements.radius?.addEventListener("change", () => this.applyFilters(true));
      this.elements.locate?.addEventListener("click", () => this.useCurrentLocation());
      this.elements.reset?.addEventListener("click", () => this.reset());

      this.elements.filterInputs.forEach((input) => {
        input.addEventListener("change", () => this.applyFilters(false));
      });

      this.elements.filterToggle?.addEventListener("click", () => {
        const expanded = this.elements.filterToggle.getAttribute("aria-expanded") === "true";
        this.elements.filterToggle.setAttribute("aria-expanded", String(!expanded));
        this.elements.filters.hidden = expanded;
      });

      this.elements.dialogClose?.addEventListener("click", () => this.closeDialog());
      this.elements.dialogPrevious?.addEventListener("click", () => this.openAdjacentMeeting(-1));
      this.elements.dialogNext?.addEventListener("click", () => this.openAdjacentMeeting(1));
      this.elements.dialog?.addEventListener("click", (event) => {
        if (event.target === this.elements.dialog) this.closeDialog();
      });
      this.elements.dialogContent?.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-report-open]");
        if (!trigger) return;
        event.preventDefault();
        const meeting = this.meetings.find((item) => item.id === this.activeMeetingId);
        if (meeting) this.openReportDialog(meeting);
      });
      this.elements.reportDialogClose?.addEventListener("click", () => this.closeReportDialog());
      this.elements.reportDialog?.addEventListener("click", (event) => {
        if (event.target === this.elements.reportDialog) this.closeReportDialog();
      });

      this.elements.viewButtons.forEach((button) => {
        button.addEventListener("click", () => this.setView(button.dataset.viewButton));
      });

      this.elements.mapFullscreen?.addEventListener("click", () => this.toggleMapFullscreen());
      this.elements.searchPanelToggle?.addEventListener("click", () => {
        this.setSearchPanelCollapsed(this.elements.searchPanel?.dataset.collapsed !== "true");
      });
      document.addEventListener("keydown", this.onDocumentKeydown);

      document.addEventListener("shopify:section:unload", (event) => {
        if (event.detail?.sectionId === this.id) this.destroy();
      });
    }

    async loadPlaces() {
      const url = this.root.dataset.placesUrl;
      if (!url) return;
      this.abortController = new AbortController();
      try {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const rows = await response.json();
        this.places = rows.map((row) => ({
          postalCode: String(row[0]),
          name: String(row[1]),
          country: String(row[2]),
          lat: Number(row[3]),
          lon: Number(row[4]),
          searchable: normalize(`${row[0]} ${row[1]} ${COUNTRY_NAMES[row[2]] || row[2]}`),
        }));
        this.applyApproximatePositions();
        this.applyFilters(false);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Der DACH-Ortsindex konnte nicht geladen werden.", error);
          this.setStatus(str("places_unavailable", "Die Ortssuche ist gerade nicht verfügbar. Filter und Karte funktionieren weiterhin."));
        }
      }
    }

    applyApproximatePositions() {
      this.meetings = this.meetings.map(meeting => {
        if (hasPreciseCoordinates(meeting)) return meeting;
        const place = approximatePlace(meeting, this.places);
        if (!place) return meeting;
        // Display-only fallback from the current address. Never persist this
        // town centre as a verified house number or reuse stale coordinates.
        return {...meeting, ...place, coordinatePrecision: "approximate", checkedAddress: {
          strasse_hausnummer: meeting.street, postleitzahl: meeting.postalCode,
          ort: meeting.city, land: meeting.country,
        }};
      });
    }

    buildBaseStyle() {
      const pmtilesUrl = this.root.dataset.pmtilesUrl?.trim();
      const rasterTilesUrl = this.root.dataset.rasterTilesUrl?.trim();
      const style = {
        version: 8,
        sources: {},
        layers: [{ id: "background", type: "background", paint: { "background-color": "#eef3ee" } }],
      };

      if (pmtilesUrl) {
        style.glyphs = "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf";
        style.sources.basemap = {
          type: "vector",
          url: `pmtiles://${pmtilesUrl}`,
          attribution: `<a href="https://protomaps.com">Protomaps</a> © <a href="https://www.openstreetmap.org/copyright">${escapeHtml(str("osm_contributors", "OpenStreetMap-Mitwirkende"))}</a>`,
        };
        style.layers.push(
          { id: "earth", type: "fill", source: "basemap", "source-layer": "earth", paint: { "fill-color": "#f4f6ef" } },
          { id: "landcover", type: "fill", source: "basemap", "source-layer": "landcover", paint: { "fill-color": "#e6efe3", "fill-opacity": 0.72 } },
          { id: "landuse", type: "fill", source: "basemap", "source-layer": "landuse", paint: { "fill-color": "#f0f0e4", "fill-opacity": 0.58 } },
          { id: "water", type: "fill", source: "basemap", "source-layer": "water", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": "#d7e4f4" } },
          { id: "water-lines", type: "line", source: "basemap", "source-layer": "water", filter: ["==", ["geometry-type"], "LineString"], paint: { "line-color": "#a8c6e4", "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.4, 12, 1.6] } },
          { id: "buildings", type: "fill", source: "basemap", "source-layer": "buildings", minzoom: 10, paint: { "fill-color": "#deded8", "fill-opacity": 0.72 } },
          { id: "boundaries", type: "line", source: "basemap", "source-layer": "boundaries", paint: { "line-color": "#94a39f", "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.7, 10, 1.3], "line-dasharray": [3, 2] } },
          { id: "roads-casing", type: "line", source: "basemap", "source-layer": "roads", filter: ["in", ["get", "kind"], ["literal", ["highway", "major_road", "minor_road"]]], paint: { "line-color": "#d7d7d1", "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.8, 12, 3.6] } },
          { id: "roads", type: "line", source: "basemap", "source-layer": "roads", paint: { "line-color": "#ffffff", "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.4, 12, 2.5] } },
          {
            id: "places",
            type: "symbol",
            source: "basemap",
            "source-layer": "places",
            layout: {
              "text-field": ["coalesce", ["get", "name:de"], ["get", "name"]],
              "text-font": ["Noto Sans Regular"],
              "text-size": ["interpolate", ["linear"], ["zoom"], 4, 10, 10, 13],
              "text-allow-overlap": false,
            },
            paint: { "text-color": "#46504d", "text-halo-color": "#ffffff", "text-halo-width": 1.2 },
          }
        );
      } else if (rasterTilesUrl) {
        style.sources.basemapRaster = {
          type: "raster",
          tiles: [rasterTilesUrl],
          tileSize: 256,
          maxzoom: 19,
          attribution: rasterTilesUrl.includes("cartocdn.com")
            ? `© CARTO · © ${str("osm_contributors", "OpenStreetMap-Mitwirkende")}`
            : `© ${str("osm_contributors", "OpenStreetMap-Mitwirkende")}`,
        };
        style.layers.push({
          id: "basemap-raster",
          type: "raster",
          source: "basemapRaster",
          paint: { "raster-saturation": -0.08, "raster-contrast": 0.04 },
        });
      } else {
        const outlineUrl = this.root.dataset.outlineUrl;
        if (outlineUrl) {
          style.sources.dach = { type: "geojson", data: outlineUrl };
          style.layers.push(
            { id: "dach-fill", type: "fill", source: "dach", paint: { "fill-color": "#f0ffb6", "fill-opacity": 0.72 } },
            { id: "dach-border", type: "line", source: "dach", paint: { "line-color": "#00965e", "line-width": 1.5 } }
          );
        }
      }
      return style;
    }

    async initMap() {
      if (!this.elements.map || !window.maplibregl) {
        this.showMapMessage(str("map_library_error", "Die Kartenbibliothek konnte nicht geladen werden."));
        return;
      }

      const pmtilesUrl = this.root.dataset.pmtilesUrl?.trim();
      const mapStyleUrl = this.root.dataset.mapStyleUrl?.trim();
      const rasterTilesUrl = this.root.dataset.rasterTilesUrl?.trim();
      if (pmtilesUrl && window.pmtiles?.Protocol) {
        this.pmtilesProtocol = new window.pmtiles.Protocol();
        window.maplibregl.addProtocol("pmtiles", this.pmtilesProtocol.tile);
      }

      this.map = new window.maplibregl.Map({
        container: this.elements.map,
        style: mapStyleUrl || this.buildBaseStyle(),
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        minZoom: 2.5,
        maxZoom: 16,
        maxBounds: [[-5, 38], [27, 62]],
        attributionControl: false,
      });
      this.map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), "top-right");
      this.map.addControl(new window.maplibregl.ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-left");
      const osmContributors = escapeHtml(str("osm_contributors", "OpenStreetMap-Mitwirkende"));
      const localPreview = escapeHtml(str("attribution_local_preview", "Lokale Vorschau"));
      this.map.addControl(
        new window.maplibregl.AttributionControl({
          compact: true,
          customAttribution: (mapStyleUrl
            ? `${localPreview} · © CARTO · © ${osmContributors}`
            : pmtilesUrl
            ? `© Protomaps · © ${osmContributors}`
            : rasterTilesUrl
              ? rasterTilesUrl.includes("cartocdn.com")
                ? `${localPreview} · © CARTO · © ${osmContributors}`
                : `${escapeHtml(str("attribution_detail_preview", "Detailvorschau"))} · © ${osmContributors}`
              : `${escapeHtml(str("attribution_map_preview", "Kartenvorschau"))} · © Natural Earth`) + (this.root.dataset.meetingsSource === "metaobjects" ? ' · <a href="https://www.geoapify.com/" target="_blank" rel="noopener">Geoapify</a>' : ""),
        }),
        "bottom-right"
      );
      this.map.on("load", () => {
        this.mapReady = true;
        this.syncMarkers();
        if (!mapStyleUrl && !pmtilesUrl && !rasterTilesUrl) {
          this.showMapMessage(str("map_preview_message", "Vorschaukarte aktiv. Für den Livegang die selbst gehostete DACH-PMTiles-Datei hinterlegen."));
        }
      });
      this.map.on("error", (event) => console.error("Kartenfehler", event.error));
    }

    showMapMessage(message) {
      if (!this.elements.mapMessage) return;
      this.elements.mapMessage.textContent = message;
      this.elements.mapMessage.hidden = false;
    }

    showSuggestions() {
      if (!this.elements.searchInput || !this.elements.suggestions) return;
      const query = normalize(this.elements.searchInput.value);
      if (query.length < 2 || !this.places.length) {
        this.hideSuggestions();
        return;
      }
      const direct = [];
      const partial = [];
      for (const place of this.places) {
        if (place.searchable.startsWith(query) || normalize(place.name).startsWith(query) || place.postalCode.startsWith(query)) {
          direct.push(place);
        } else if (partial.length < 8 && place.searchable.includes(query)) {
          partial.push(place);
        }
        if (direct.length >= 8) break;
      }
      const matches = direct.concat(partial).slice(0, 8);
      if (!matches.length) {
        this.hideSuggestions();
        return;
      }

      this.activeSuggestionIndex = -1;
      this.elements.suggestions.innerHTML = "";
      matches.forEach((place, index) => {
        const item = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.id = `stm-suggestion-${this.id}-${index}`;
        button.setAttribute("role", "option");
        button.setAttribute("aria-selected", "false");
        button.textContent = `${place.postalCode} ${place.name}, ${countryName(place.country)}`;
        button.addEventListener("mousedown", (event) => event.preventDefault());
        button.addEventListener("click", () => this.selectPlace(place));
        item.append(button);
        this.elements.suggestions.append(item);
      });
      this.elements.suggestions.hidden = false;
      this.elements.searchInput.setAttribute("aria-expanded", "true");
    }

    onSearchKeydown(event) {
      if (this.elements.suggestions?.hidden) return;
      const buttons = Array.from(this.elements.suggestions.querySelectorAll("button"));
      if (!buttons.length) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const direction = event.key === "ArrowDown" ? 1 : -1;
        this.activeSuggestionIndex = (this.activeSuggestionIndex + direction + buttons.length) % buttons.length;
        buttons.forEach((button, index) => button.setAttribute("aria-selected", String(index === this.activeSuggestionIndex)));
        this.elements.searchInput.setAttribute("aria-activedescendant", buttons[this.activeSuggestionIndex].id);
      } else if (event.key === "Enter" && this.activeSuggestionIndex >= 0) {
        event.preventDefault();
        buttons[this.activeSuggestionIndex].click();
      } else if (event.key === "Escape") {
        this.hideSuggestions();
      }
    }

    hideSuggestions() {
      if (!this.elements.suggestions || !this.elements.searchInput) return;
      this.elements.suggestions.hidden = true;
      this.elements.searchInput.setAttribute("aria-expanded", "false");
      this.elements.searchInput.removeAttribute("aria-activedescendant");
      this.activeSuggestionIndex = -1;
    }

    selectPlace(place) {
      this.elements.searchInput.value = `${place.postalCode} ${place.name}`;
      this.searchCenter = { lat: place.lat, lon: place.lon, label: `${place.postalCode} ${place.name}` };
      this.hideSuggestions();
      this.applyFilters(true);
    }

    applyTypedSearch() {
      const query = normalize(this.elements.searchInput?.value);
      if (!query) {
        this.searchCenter = null;
        this.applyFilters(true);
        return;
      }
      const exact = this.places.find((place) =>
        place.postalCode === query ||
        normalize(place.name) === query ||
        normalize(`${place.postalCode} ${place.name}`) === query
      );
      const starts = exact || this.places.find((place) => place.searchable.startsWith(query));
      if (!starts) {
        this.setStatus(str("place_not_found", "Ort oder PLZ nicht gefunden. Bitte einen Vorschlag aus der Liste auswählen."));
        this.elements.searchInput?.setAttribute("aria-invalid", "true");
        return;
      }
      this.elements.searchInput?.removeAttribute("aria-invalid");
      this.selectPlace(starts);
    }

    useCurrentLocation() {
      if (!navigator.geolocation) {
        this.setStatus(str("geolocation_unsupported", "Die Standortbestimmung wird von diesem Browser nicht unterstützt."));
        return;
      }
      this.elements.locate.disabled = true;
      this.setStatus(str("locating", "Standort wird ermittelt …"));
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.searchCenter = {
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            label: str("your_location", "Dein Standort"),
          };
          this.elements.searchInput.value = str("my_location", "Mein Standort");
          this.elements.locate.disabled = false;
          this.applyFilters(true);
        },
        () => {
          this.elements.locate.disabled = false;
          this.setStatus(str("location_failed", "Standort konnte nicht ermittelt werden. Bitte Ort oder PLZ eingeben."));
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    }

    getFilters() {
      return Object.fromEntries(this.elements.filterInputs.map((input) => [input.value, input.checked]));
    }

    applyFilters(moveMap) {
      const filters = this.getFilters();
      const techniqueSelected = ["knitting", "crochet", "crafts"].filter((key) => filters[key]);
      const radius = Number(this.elements.radius?.value || 50);
      const hasRadius = this.searchCenter && Number.isFinite(radius) && radius > 0;

      this.filteredMeetings = this.meetings
        .map((meeting) => ({
          ...meeting,
          distance: this.searchCenter && hasCoordinates(meeting) ? haversine(this.searchCenter, meeting) : null,
        }))
        .filter((meeting) => {
          if (hasRadius && (meeting.distance === null || meeting.distance > radius)) return false;
          if (techniqueSelected.length && !techniqueSelected.some((key) => meeting[key])) return false;
          if (filters.beginner && !meeting.beginner) return false;
          if (filters.free && !meeting.free) return false;
          if (filters.accessible && !meeting.accessible) return false;
          if (filters.registration && !meeting.registration) return false;
          return true;
        })
        .sort((a, b) => {
          if (this.searchCenter) return a.distance - b.distance;
          return a.name.localeCompare(b.name, "de");
        });

      this.renderResults();
      this.syncMarkers();
      if (moveMap) this.fitMap();
    }

    renderResults() {
      if (!this.elements.results) return;
      this.elements.results.innerHTML = "";

      if (!this.filteredMeetings.length) {
        const empty = document.createElement("li");
        empty.className = "stricktreffen-map__empty";
        empty.textContent = this.meetings.length
          ? str("no_results", "Keine passenden Treffen gefunden. Vergrößere den Umkreis oder entferne einen Filter.")
          : str("no_meetings", "Noch sind keine Treffen veröffentlicht. Kennst du eines? Dann reiche es gerne ein.");
        this.elements.results.append(empty);
      } else {
        this.filteredMeetings.forEach((meeting) => {
          const item = document.createElement("li");
          const button = document.createElement("button");
          button.type = "button";
          button.className = "stricktreffen-map__result-card";
          button.dataset.meetingId = meeting.id;
          button.dataset.active = String(meeting.id === this.activeMeetingId);
          button.setAttribute("aria-label", str("open_details", "[name], Details öffnen", { name: meeting.name }));
          button.innerHTML = this.cardTemplate(meeting);
          button.addEventListener("click", () => this.openMeeting(meeting.id));
          button.addEventListener("mouseenter", () => this.highlightMeeting(meeting.id));
          button.addEventListener("mouseleave", () => this.highlightMeeting(null));
          item.append(button);
          this.elements.results.append(item);
        });
      }

      const count = this.filteredMeetings.length;
      const mappedCount = this.filteredMeetings.filter(hasCoordinates).length;
      if (this.elements.resultsCount) {
        this.elements.resultsCount.textContent = count === 1
          ? str("results_count_one", "[count] Treffer", { count })
          : str("results_count_other", "[count] Treffer", { count });
      }
      const plural = count === 1 ? "one" : "other";
      const found = this.searchCenter
        ? str(`status_found_near_${plural}`, "[count] Treffen rund um [place] gefunden.", { count, place: this.searchCenter.label })
        : str(`status_found_${plural}`, "[count] Treffen gefunden.", { count });
      const mapContext = mappedCount < count ? ` ${str("status_on_map", "[mapped] davon sind auf der Karte verfügbar.", { mapped: mappedCount })}` : "";
      const compact = this.searchCenter
        ? str(`status_compact_near_${plural}`, "[count] Treffen im Umkreis", { count })
        : str(`status_compact_${plural}`, "[count] Treffen", { count });
      const compactMapContext = mappedCount < count ? ` · ${str("status_compact_on_map", "[mapped] auf Karte", { mapped: mappedCount })}` : "";
      this.setStatus(`${found}${mapContext}`, `${compact}${compactMapContext}`);
    }

    cardTemplate(meeting) {
      const badges = this.badgesFor(meeting)
        .slice(0, 4)
        .map((badge) => `<span class="stricktreffen-map__badge">${escapeHtml(badge)}</span>`)
        .join("");
      const distance = Number.isFinite(meeting.distance)
        ? `<span class="stricktreffen-map__distance">${hasPreciseCoordinates(meeting) ? "" : `${escapeHtml(str("approx", "ca."))} `}${meeting.distance < 10 ? meeting.distance.toFixed(1) : Math.round(meeting.distance)} km</span>`
        : "";
      return `
        <span class="stricktreffen-map__result-top">
          <h3>${escapeHtml(meeting.name)}</h3>${distance}
        </span>
        <span class="stricktreffen-map__meta">
          <span>${iconTemplate("location")}<span>${escapeHtml([meeting.postalCode, meeting.city].filter(Boolean).join(" "))}</span></span>
          <span>${iconTemplate("clock")}<span>${escapeHtml(meeting.schedule || str("schedule_ask", "Termin beim Veranstalter erfragen"))}</span></span>
        </span>
        <span class="stricktreffen-map__badges">${meeting.socialProject ? '<span class="stricktreffen-map__badge stricktreffen-map__badge--social">Maschen für die Gesellschaft</span>' : ""}${badges}</span>`;
    }

    badgesFor(meeting) {
      const badges = [];
      if (meeting.knitting) badges.push(str("filter_knitting", "Stricken"));
      if (meeting.crochet) badges.push(str("filter_crochet", "Häkeln"));
      if (meeting.crafts) badges.push(str("filter_crafts", "Handarbeit"));
      if (meeting.beginner) badges.push(str("filter_beginner", "Für Anfänger"));
      if (meeting.free) badges.push(str("filter_free", "Kostenlos"));
      if (meeting.accessible) badges.push(str("filter_accessible", "Barrierefrei"));
      return badges;
    }

    syncMarkers() {
      if (!this.mapReady || !this.map) return;
      const mappableMeetings = this.filteredMeetings.filter(hasCoordinates);
      const visibleIds = new Set(mappableMeetings.map((meeting) => meeting.id));
      this.markers.forEach((marker, id) => {
        if (!visibleIds.has(id)) {
          marker.remove();
          this.markers.delete(id);
        }
      });
      mappableMeetings.forEach((meeting) => {
        if (this.markers.has(meeting.id)) return;
        const element = document.createElement("button");
        element.type = "button";
        element.className = "stricktreffen-map__marker";
        element.dataset.socialProject = String(meeting.socialProject);
        element.dataset.approximate = String(!hasPreciseCoordinates(meeting));
        element.title = meeting.name;
        element.setAttribute("aria-label", str("open_details", "[name], Details öffnen", { name: `${meeting.name}${meeting.socialProject ? ", Maschen für die Gesellschaft" : ""}` }));
        element.innerHTML = '<span aria-hidden="true"></span>';
        element.addEventListener("click", () => this.openMeeting(meeting.id));
        element.addEventListener("mouseenter", () => this.highlightMeeting(meeting.id));
        element.addEventListener("mouseleave", () => this.highlightMeeting(null));
        const marker = new window.maplibregl.Marker({ element, anchor: "bottom" })
          .setLngLat([meeting.lon, meeting.lat])
          .addTo(this.map);
        this.markers.set(meeting.id, marker);
      });
    }

    fitMap() {
      if (!this.mapReady || !this.map) return;
      if (this.searchCenter) {
        const radius = Number(this.elements.radius?.value || 50);
        const zoom = radius <= 10 ? 10 : radius <= 25 ? 8.5 : radius <= 50 ? 7.5 : radius <= 100 ? 6.4 : 5.5;
        this.map.flyTo({ center: [this.searchCenter.lon, this.searchCenter.lat], zoom, essential: false });
      } else if (this.filteredMeetings.length > 1) {
        const bounds = new window.maplibregl.LngLatBounds();
        const mappableMeetings = this.filteredMeetings.filter(hasCoordinates);
        mappableMeetings.forEach((meeting) => bounds.extend([meeting.lon, meeting.lat]));
        if (mappableMeetings.length) this.map.fitBounds(bounds, { padding: 70, maxZoom: 9, duration: 0 });
      }
    }

    highlightMeeting(id) {
      this.root.querySelectorAll("[data-meeting-id]").forEach((card) => {
        card.dataset.active = String(card.dataset.meetingId === id);
      });
      this.markers.forEach((marker, markerId) => {
        marker.getElement().dataset.active = String(markerId === id);
      });
    }

    openMeeting(id) {
      const meeting = this.meetings.find((item) => item.id === id);
      if (!meeting || !this.elements.dialogContent) return;
      this.activeMeetingId = id;
      this.highlightMeeting(id);
      this.elements.dialogContent.innerHTML = this.detailTemplate(meeting);
      this.bindDetailGallery();
      this.updateDetailNavigation();
      if (typeof this.elements.dialog?.showModal === "function" && !this.elements.dialog.open) {
        this.elements.dialog.showModal();
      } else if (!this.elements.dialog?.hasAttribute("open")) {
        this.elements.dialog?.setAttribute("open", "");
      }
      this.elements.dialog?.scrollTo({ top: 0, behavior: "auto" });
      this.elements.dialogContent?.scrollTo({ top: 0, behavior: "auto" });
      if (this.mapReady && hasCoordinates(meeting)) {
        this.map.flyTo({ center: [meeting.lon, meeting.lat], zoom: Math.max(this.map.getZoom(), 10), essential: false });
      }
    }

    closeDialog() {
      if (typeof this.elements.dialog?.close === "function") this.elements.dialog.close();
      else this.elements.dialog?.removeAttribute("open");
      this.activeMeetingId = null;
      this.highlightMeeting(null);
    }

    openReportDialog(meeting) {
      if (!this.elements.reportDialog) return;
      if (this.elements.reportMeeting) this.elements.reportMeeting.value = meeting.name;
      if (this.elements.reportMeetingDisplay) this.elements.reportMeetingDisplay.value = meeting.name;
      this.closeDialog();
      if (typeof this.elements.reportDialog.showModal === "function" && !this.elements.reportDialog.open) {
        this.elements.reportDialog.showModal();
      } else if (!this.elements.reportDialog.hasAttribute("open")) {
        this.elements.reportDialog.setAttribute("open", "");
      }
      window.setTimeout(() => this.elements.reportEmail?.focus(), 0);
    }

    closeReportDialog() {
      if (typeof this.elements.reportDialog?.close === "function") this.elements.reportDialog.close();
      else this.elements.reportDialog?.removeAttribute("open");
    }

    showPostedFormFeedback() {
      const isReportResponse = window.location.hash === "#stricktreffen-report-form";
      if (isReportResponse && this.elements.reportFeedback && this.elements.reportDialog) {
        if (this.elements.submissionSuccess) this.elements.submissionSuccess.hidden = true;
        if (typeof this.elements.reportDialog.showModal === "function" && !this.elements.reportDialog.open) {
          this.elements.reportDialog.showModal();
        } else if (!this.elements.reportDialog.hasAttribute("open")) {
          this.elements.reportDialog.setAttribute("open", "");
        }
        window.setTimeout(() => this.elements.reportFeedback?.focus(), 0);
      }
    }

    detailNavigationMeetings() {
      return this.filteredMeetings.length ? this.filteredMeetings : this.meetings;
    }

    updateDetailNavigation() {
      const meetings = this.detailNavigationMeetings();
      const index = meetings.findIndex((meeting) => meeting.id === this.activeMeetingId);
      const disabled = meetings.length < 2 || index < 0;
      if (this.elements.dialogPrevious) this.elements.dialogPrevious.disabled = disabled;
      if (this.elements.dialogNext) this.elements.dialogNext.disabled = disabled;
      if (this.elements.dialogPosition) {
        this.elements.dialogPosition.textContent = index >= 0 ? `${index + 1} / ${meetings.length}` : "";
      }
      if (!disabled) {
        const previous = meetings[(index - 1 + meetings.length) % meetings.length];
        const next = meetings[(index + 1) % meetings.length];
        this.elements.dialogPrevious?.setAttribute("aria-label", str("previous_meeting_named", "Vorheriges Treffen: [name]", { name: previous.name }));
        this.elements.dialogNext?.setAttribute("aria-label", str("next_meeting_named", "Nächstes Treffen: [name]", { name: next.name }));
      }
    }

    openAdjacentMeeting(direction) {
      const meetings = this.detailNavigationMeetings();
      if (meetings.length < 2) return;
      const index = meetings.findIndex((meeting) => meeting.id === this.activeMeetingId);
      if (index < 0) return;
      const nextIndex = (index + direction + meetings.length) % meetings.length;
      this.openMeeting(meetings[nextIndex].id);
    }

    galleryImages(meeting) {
      const values = [meeting.imageUrl, ...(Array.isArray(meeting.imageUrls) ? meeting.imageUrls : [])];
      const themeImages = {
        "../assets/stricktreffen-gallery-gruendl-people.jpg": this.root.dataset.galleryPeopleUrl,
        "../assets/stricktreffen-gallery-gruendl-projects.jpg": this.root.dataset.galleryProjectsUrl,
        "../assets/stricktreffen-gallery-gruendl-yarn.jpg": this.root.dataset.galleryYarnUrl,
      };
      return [...new Set(values.map((value) => safeUrl(themeImages[value] || value, ["http:", "https:"])).filter(Boolean))];
    }

    galleryTemplate(meeting) {
      const images = this.galleryImages(meeting);
      if (!images.length) return "";
      const multiple = images.length > 1;
      const thumbnails = images.map((image, index) => `
        <button class="stricktreffen-map__gallery-thumb" type="button" data-gallery-thumb="${index}" aria-label="${escapeHtml(str("gallery_thumb_aria", "Bild [index] von [total] anzeigen", { index: index + 1, total: images.length }))}" aria-current="${index === 0 ? "true" : "false"}">
          <img src="${escapeHtml(image)}" alt="" loading="lazy">
        </button>`).join("");
      return `
        <section class="stricktreffen-map__gallery" data-detail-gallery data-gallery-index="0" aria-label="${escapeHtml(str("gallery_aria", "Bildergalerie: [name]", { name: meeting.name }))}" tabindex="0">
          <div class="stricktreffen-map__gallery-stage">
            <img class="stricktreffen-map__gallery-image" data-gallery-image src="${escapeHtml(images[0])}" alt="${escapeHtml(str("gallery_image_alt", "[name] – Bild [index] von [total]", { name: meeting.name, index: 1, total: images.length }))}">
            ${multiple ? `<button class="stricktreffen-map__gallery-nav stricktreffen-map__gallery-nav--previous" type="button" data-gallery-previous aria-label="${escapeHtml(str("previous_image", "Vorheriges Bild"))}">←</button><button class="stricktreffen-map__gallery-nav stricktreffen-map__gallery-nav--next" type="button" data-gallery-next aria-label="${escapeHtml(str("next_image", "Nächstes Bild"))}">→</button><span class="stricktreffen-map__gallery-count" data-gallery-count aria-live="polite">1 / ${images.length}</span>` : ""}
          </div>
          ${multiple ? `<div class="stricktreffen-map__gallery-thumbs" role="list">${thumbnails}</div>` : ""}
        </section>`;
    }

    bindDetailGallery() {
      const gallery = this.elements.dialogContent?.querySelector("[data-detail-gallery]");
      if (!gallery) return;
      const mainImage = gallery.querySelector("[data-gallery-image]");
      const count = gallery.querySelector("[data-gallery-count]");
      const thumbnails = Array.from(gallery.querySelectorAll("[data-gallery-thumb]"));
      if (!mainImage || thumbnails.length < 2) return;
      const show = (nextIndex) => {
        const index = (nextIndex + thumbnails.length) % thumbnails.length;
        const image = thumbnails[index].querySelector("img");
        if (!image) return;
        gallery.dataset.galleryIndex = String(index);
        mainImage.src = image.src;
        mainImage.alt = str("gallery_image_alt", "[name] – Bild [index] von [total]", {
          name: this.meetings.find((meeting) => meeting.id === this.activeMeetingId)?.name || str("type_knitting", "Stricktreffen"),
          index: index + 1,
          total: thumbnails.length,
        });
        if (count) count.textContent = `${index + 1} / ${thumbnails.length}`;
        thumbnails.forEach((thumbnail, thumbnailIndex) => thumbnail.setAttribute("aria-current", String(thumbnailIndex === index)));
      };
      thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener("click", () => show(index)));
      gallery.querySelector("[data-gallery-previous]")?.addEventListener("click", () => show(Number(gallery.dataset.galleryIndex) - 1));
      gallery.querySelector("[data-gallery-next]")?.addEventListener("click", () => show(Number(gallery.dataset.galleryIndex) + 1));
      gallery.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        show(Number(gallery.dataset.galleryIndex) + (event.key === "ArrowRight" ? 1 : -1));
      });
    }

    detailTemplate(meeting) {
      const publicEmail = meeting.publicEmail ? `mailto:${meeting.publicEmail}` : "";
      const phone = meeting.publicPhone ? `tel:${meeting.publicPhone.replace(/[^+\d]/g, "")}` : "";
      const website = safeUrl(meeting.website, ["http:", "https:"]);
      const registrationUrl = safeUrl(meeting.registrationUrl, ["http:", "https:"]);
      const socialUrl = safeUrl(meeting.socialUrl, ["http:", "https:"]);
      const routeUrl = hasPreciseCoordinates(meeting)
        ? `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=;${encodeURIComponent(`${meeting.lat},${meeting.lon}`)}`
        : "";
      const cost = meeting.free === true
        ? str("filter_free", "Kostenlos")
        : meeting.free === false
          ? meeting.fee || str("cost_paid_ask", "Kostenpflichtig – Betrag bitte erfragen")
          : meeting.fee || str("cost_ask", "Kosten bitte beim Veranstalter erfragen");
      const registration = meeting.registration === true
        ? str("filter_registration", "Anmeldung erforderlich")
        : meeting.registration === false
          ? str("registration_not_required", "Keine Anmeldung erforderlich")
          : str("registration_ask", "Anmeldung bitte beim Veranstalter erfragen");
      const beginner = meeting.beginner === true
        ? str("beginner_suitable", "Für Anfänger geeignet")
        : meeting.beginner === false
          ? str("beginner_experience", "Vorkenntnisse empfohlen")
          : str("beginner_ask", "Eignung für Anfänger bitte erfragen");
      const repeatedDetailValues = [meeting.registrationInfo, meeting.materialInfo, meeting.consumptionInfo].map(normalize).filter(Boolean);
      const description = meeting.description && !repeatedDetailValues.includes(normalize(meeting.description)) ? meeting.description : "";
      const address = [meeting.venue, meeting.street, [meeting.postalCode, meeting.city].filter(Boolean).join(" "), countryName(meeting.country)]
        .filter(Boolean)
        .map(escapeHtml)
        .join("<br>");
      const contactLines = [
        meeting.publicContact && `<span>${iconTemplate("person")}<span><strong>${escapeHtml(str("contact_person", "Ansprechpartner"))}</strong>${escapeHtml(meeting.publicContact)}</span></span>`,
        publicEmail && `<span>${iconTemplate("mail")}<span><strong>${escapeHtml(str("email", "E-Mail"))}</strong><a href="${escapeHtml(publicEmail)}">${escapeHtml(meeting.publicEmail)}</a></span></span>`,
        phone && `<span>${iconTemplate("phone")}<span><strong>${escapeHtml(str("phone", "Telefon"))}</strong><a href="${escapeHtml(phone)}">${escapeHtml(meeting.publicPhone)}</a></span></span>`,
        website && `<span>${iconTemplate("link")}<span><strong>${escapeHtml(str("label_website", "Website"))}</strong><a href="${escapeHtml(website)}" target="_blank" rel="noopener">${escapeHtml(str("open_website", "Website öffnen"))}</a></span></span>`,
        socialUrl && `<span>${iconTemplate("link")}<span><strong>${escapeHtml(str("social_media", "Social Media"))}</strong><a href="${escapeHtml(socialUrl)}" target="_blank" rel="noopener">${escapeHtml(str("open_profile", "Profil öffnen"))}</a></span></span>`,
      ].filter(Boolean).join("");
      const participationDetails = [
        escapeHtml(cost),
        escapeHtml(registration),
        meeting.registrationInfo ? escapeHtml(meeting.registrationInfo) : "",
        registrationUrl ? `<a href="${escapeHtml(registrationUrl)}" target="_blank" rel="noopener">${escapeHtml(str("open_registration_info", "Anmeldeinformationen öffnen"))}</a>` : "",
      ].filter(Boolean).join("<br>");
      const usefulDetails = [
        escapeHtml(beginner),
        meeting.accessibilityInfo ? escapeHtml(meeting.accessibilityInfo) : "",
        meeting.materialInfo ? escapeHtml(meeting.materialInfo) : "",
        meeting.consumptionInfo ? escapeHtml(meeting.consumptionInfo) : "",
      ].filter(Boolean).join("<br>");
      return `
        <h2 class="stricktreffen-map__detail-title">${escapeHtml(meeting.name)}</h2>
        ${this.galleryTemplate(meeting)}
        <div class="stricktreffen-map__badges" style="margin-top:1rem">${meeting.socialProject ? '<span class="stricktreffen-map__badge stricktreffen-map__badge--social">Maschen für die Gesellschaft</span>' : ""}${this.badgesFor(meeting).map((badge) => `<span class="stricktreffen-map__badge">${escapeHtml(badge)}</span>`).join("")}</div>
        ${description ? `<p class="stricktreffen-map__detail-description">${escapeHtml(description)}</p>` : ""}
        <div class="stricktreffen-map__detail-grid">
          <section class="stricktreffen-map__detail-row">${iconTemplate("location")}<div><h3>${escapeHtml(str("detail_location", "Ort"))}</h3><p>${address}</p>${routeUrl ? `<a class="stricktreffen-map__button stricktreffen-map__button--secondary stricktreffen-map__route-button" href="${escapeHtml(routeUrl)}" target="_blank" rel="noopener">${escapeHtml(str("plan_route", "Route planen"))}</a>` : ""}</div></section>
          <section class="stricktreffen-map__detail-row">${iconTemplate("clock")}<div><h3>${escapeHtml(str("detail_schedule", "Termin"))}</h3><p>${escapeHtml(meeting.schedule || str("schedule_ask_short", "Bitte beim Veranstalter erfragen"))}${meeting.recurrence ? `<br>${escapeHtml(meeting.recurrence)}` : ""}</p></div></section>
          <section class="stricktreffen-map__detail-row">${iconTemplate("people")}<div><h3>${escapeHtml(str("step_title_participation", "Teilnahme"))}</h3><p>${participationDetails}</p></div></section>
          <section class="stricktreffen-map__detail-row">${iconTemplate("info")}<div><h3>${escapeHtml(str("good_to_know", "Gut zu wissen"))}</h3><p>${usefulDetails}</p></div></section>
        </div>
        ${contactLines ? `<section class="stricktreffen-map__contact"><h3>${escapeHtml(str("step_title_contact", "Kontakt"))}</h3><div>${contactLines}</div></section>` : ""}
        <button class="stricktreffen-map__report-link" type="button" data-report-open>${iconTemplate("flag")}<span>${escapeHtml(str("report_title", "Fehler melden oder Angaben korrigieren"))}</span></button>
        ${meeting.lastVerified ? `<p class="stricktreffen-map__help stricktreffen-map__updated">${escapeHtml(str("last_updated", "Zuletzt aktualisiert: [date]", { date: meeting.lastVerified }))}</p>` : ""}`;
    }

    setView(view) {
      if (!this.elements.layout) return;
      this.elements.layout.dataset.view = view;
      this.elements.viewButtons.forEach((button) => {
        const active = button.dataset.viewButton === view;
        button.setAttribute("aria-selected", String(active));
      });
      if (view === "map" && this.map) window.setTimeout(() => this.map.resize(), 50);
    }

    setSearchPanelCollapsed(collapsed) {
      if (!this.elements.searchPanel || !this.elements.searchPanelToggle) return;
      this.elements.searchPanel.dataset.collapsed = String(collapsed);
      this.elements.searchPanelToggle.setAttribute("aria-expanded", String(!collapsed));
      this.elements.searchPanelToggle.setAttribute(
        "aria-label",
        collapsed ? str("panel_expand_aria", "Suche und Filter öffnen") : str("panel_collapse_aria", "Suche und Filter verkleinern")
      );
      if (this.elements.searchPanelToggleLabel) {
        this.elements.searchPanelToggleLabel.textContent = collapsed ? str("panel_expand", "Öffnen") : str("panel_collapse", "Verkleinern");
      }
    }

    toggleMapFullscreen(force) {
      if (!this.elements.finder || !this.elements.mapFullscreen) return;
      const expanded = typeof force === "boolean"
        ? force
        : this.elements.finder.dataset.fullscreen !== "true";
      this.elements.finder.dataset.fullscreen = String(expanded);
      this.elements.mapFullscreen.setAttribute("aria-pressed", String(expanded));
      this.elements.mapFullscreen.setAttribute(
        "aria-label",
        expanded ? str("fullscreen_close_aria", "Vollbildansicht schließen") : str("fullscreen_open_aria", "Suche und Karte im Vollbild öffnen")
      );
      if (this.elements.mapFullscreenLabel) {
        this.elements.mapFullscreenLabel.textContent = expanded ? str("fullscreen_close", "Vollbild schließen") : str("fullscreen", "Vollbild");
      }
      if (!expanded) this.setSearchPanelCollapsed(false);
      document.documentElement.classList.toggle("stm-map-open", expanded);
      window.setTimeout(() => this.map?.resize(), 80);
    }

    reset() {
      this.searchCenter = null;
      if (this.elements.searchInput) {
        this.elements.searchInput.value = "";
        this.elements.searchInput.removeAttribute("aria-invalid");
      }
      if (this.elements.radius) this.elements.radius.value = this.root.dataset.defaultRadius || "50";
      this.elements.filterInputs.forEach((input) => { input.checked = false; });
      this.filteredMeetings = this.meetings.map((meeting) => ({ ...meeting, distance: null }));
      this.renderResults();
      this.syncMarkers();
      if (this.mapReady) this.map.flyTo({ center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM, essential: false });
    }

    setStatus(message, compactMessage = "") {
      if (!this.elements.status) return;
      const full = document.createElement("span");
      full.className = "stricktreffen-map__status-full";
      full.textContent = message;
      this.elements.status.replaceChildren(full);
      if (compactMessage) {
        const compact = document.createElement("span");
        compact.className = "stricktreffen-map__status-short";
        compact.setAttribute("aria-hidden", "true");
        compact.textContent = compactMessage;
        this.elements.status.append(compact);
      }
      this.elements.status.classList.toggle("has-compact-status", Boolean(compactMessage));
      this.elements.status.closest(".stricktreffen-map__toolbar")?.classList.toggle("has-compact-status", Boolean(compactMessage));
    }

    destroy() {
      this.toggleMapFullscreen(false);
      document.removeEventListener("keydown", this.onDocumentKeydown);
      this.abortController?.abort();
      this.markers.forEach((marker) => marker.remove());
      this.markers.clear();
      this.map?.remove();
      if (this.pmtilesProtocol && window.maplibregl) window.maplibregl.removeProtocol("pmtiles");
    }
  }

  function boot() {
    document.querySelectorAll("[data-stricktreffen-map]").forEach((root) => {
      initFaqAccordions(root);
      new StricktreffenMap(root).init();
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
